import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTheme } from "next-themes";
import { useAgentChat } from "@/contexts/AgentChatContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useCurrentUserProfile } from "@/hooks/useCurrentUserProfile";
import {
  clearSentryFeedbackTriggerMetrics,
  getCurrentFloatingWidgetViewportOffset,
  setSentryFeedbackTriggerMetrics,
} from "@/lib/floatingWidgetLayout";
import {
  applyFloatingWidgetSuppression,
  shouldTemporarilyHideFloatingWidget,
} from "@/lib/floatingWidgetSuppression";
import { getSentryFeedbackConfig, getSentryFeedbackContextLine } from "@/lib/buildInfo";
import { resolveSentryFeedbackColorScheme } from "@/lib/sentryFeedbackTheme";

interface FeedbackWidgetController {
  el: HTMLElement;
  appendToDom: () => void;
  hide: () => void;
  removeFromDom: () => void;
  show: () => void;
}

interface FeedbackController {
  createWidget: (options: Record<string, unknown>) => FeedbackWidgetController;
  setTheme: (colorScheme: "light" | "dark" | "system") => void;
}

const SCROLL_SETTLE_DELAY_MS = 180;
const COMPACT_FEEDBACK_TRIGGER_MAX_WIDTH_PX = 1279;
const COMPACT_FEEDBACK_TRIGGER_SIZE_PX = 40;
const COMPACT_FEEDBACK_TRIGGER_STYLE_ID = "supply-guard-feedback-compact-trigger";

function installCompactTriggerStyles(triggerElement: HTMLElement) {
  const root = triggerElement.getRootNode?.();
  if (!(root instanceof ShadowRoot) || root.getElementById(COMPACT_FEEDBACK_TRIGGER_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = COMPACT_FEEDBACK_TRIGGER_STYLE_ID;
  style.textContent = `
    @media (max-width: ${COMPACT_FEEDBACK_TRIGGER_MAX_WIDTH_PX}px) {
      .widget__actor {
        box-sizing: border-box !important;
        width: ${COMPACT_FEEDBACK_TRIGGER_SIZE_PX}px !important;
        min-width: ${COMPACT_FEEDBACK_TRIGGER_SIZE_PX}px !important;
        height: ${COMPACT_FEEDBACK_TRIGGER_SIZE_PX}px !important;
        padding: 10px !important;
        border-radius: 9999px !important;
      }

      .widget__actor > svg {
        width: 20px !important;
        height: 20px !important;
        flex: 0 0 20px !important;
      }

      .widget__actor > span {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
    }
  `;

  root.appendChild(style);
}

function clearLegacyFeedbackDraft(root: HTMLElement) {
  const messageInput = root.querySelector("textarea[name='message']") as HTMLTextAreaElement | null;
  if (!messageInput) {
    return;
  }

  const normalizedValue = messageInput.value.trim();
  if (!normalizedValue) {
    return;
  }

  const legacyContextLine = getSentryFeedbackContextLine();
  if (normalizedValue === legacyContextLine) {
    messageInput.value = "";
    messageInput.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

function publishWidgetMetrics(triggerElement: HTMLElement) {
  const rect = triggerElement.getBoundingClientRect();
  setSentryFeedbackTriggerMetrics({
    bottom: Math.max(0, window.innerHeight - rect.bottom),
    height: rect.height,
    right: Math.max(0, window.innerWidth - rect.right),
    visible: rect.width > 0 && rect.height > 0,
    width: rect.width,
  });
}

export function SentryFeedbackTrigger() {
  const { isMinimized, isOpen } = useAgentChat();
  const { user } = useOrganization();
  const profile = useCurrentUserProfile(user?.id);
  const { resolvedTheme, theme = "system" } = useTheme();
  const widgetRef = useRef<FeedbackWidgetController | null>(null);
  const rafRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const isTemporarilySuppressedRef = useRef(false);
  const shouldHideTrigger = isOpen && !isMinimized;
  const colorScheme = resolveSentryFeedbackColorScheme(theme, resolvedTheme);

  useEffect(() => {
    const username = profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : user?.email ?? undefined;

    if (!user?.id) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username,
    });
  }, [profile?.first_name, profile?.last_name, user?.email, user?.id]);

  useEffect(() => {
    const feedback = Sentry.getFeedback() as FeedbackController | undefined;
    if (!feedback || widgetRef.current) {
      return;
    }

    const widget = feedback.createWidget({
      colorScheme,
      showBranding: false,
      onFormOpen: () => {
        clearLegacyFeedbackDraft(widget.el);
      },
      ...getSentryFeedbackConfig(),
    });

    widget.appendToDom();
    widgetRef.current = widget;
    installCompactTriggerStyles(widget.el);

    if (shouldHideTrigger) {
      widget.hide();
    }

    const updateMetrics = () => {
      const viewportOffset = getCurrentFloatingWidgetViewportOffset();
      widget.el.style.right = `${viewportOffset.right}px`;
      widget.el.style.bottom = `${viewportOffset.bottom}px`;
      const temporarilySuppressed = shouldTemporarilyHideFloatingWidget(widget.el);
      if (isTemporarilySuppressedRef.current !== temporarilySuppressed) {
        applyFloatingWidgetSuppression(widget.el, temporarilySuppressed);
        isTemporarilySuppressedRef.current = temporarilySuppressed;
      }
      publishWidgetMetrics(widget.el);
    };

    const scheduleMetricsUpdate = () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        updateMetrics();
      });
    };

    const scheduleMetricsUpdateAfterScrollSettles = () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        scrollTimeoutRef.current = null;
        scheduleMetricsUpdate();
      }, SCROLL_SETTLE_DELAY_MS);
    };

    updateMetrics();

    const resizeObserver = new ResizeObserver(() => scheduleMetricsUpdate());
    resizeObserver.observe(widget.el);
    const mutationObserver = new MutationObserver((records) => {
      if (records.every((record) => widget.el.contains(record.target) || record.target === widget.el)) {
        return;
      }

      scheduleMetricsUpdate();
    });
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    const visualViewport = window.visualViewport;
    window.addEventListener("resize", scheduleMetricsUpdate);
    window.addEventListener("scroll", scheduleMetricsUpdateAfterScrollSettles, true);
    visualViewport?.addEventListener("resize", scheduleMetricsUpdate);
    visualViewport?.addEventListener("scroll", scheduleMetricsUpdate);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", scheduleMetricsUpdate);
      window.removeEventListener("scroll", scheduleMetricsUpdateAfterScrollSettles, true);
      visualViewport?.removeEventListener("resize", scheduleMetricsUpdate);
      visualViewport?.removeEventListener("scroll", scheduleMetricsUpdate);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      widget.removeFromDom();
      widgetRef.current = null;
      isTemporarilySuppressedRef.current = false;
      clearSentryFeedbackTriggerMetrics();
    };
  }, [shouldHideTrigger]);

  useEffect(() => {
    const feedback = Sentry.getFeedback() as FeedbackController | undefined;
    if (!feedback) {
      return;
    }

    feedback.setTheme(colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) {
      return;
    }

    if (shouldHideTrigger) {
      widget.hide();
      isTemporarilySuppressedRef.current = false;
      clearSentryFeedbackTriggerMetrics();
      return;
    }

    widget.show();
    const viewportOffset = getCurrentFloatingWidgetViewportOffset();
    widget.el.style.right = `${viewportOffset.right}px`;
    widget.el.style.bottom = `${viewportOffset.bottom}px`;
    const temporarilySuppressed = shouldTemporarilyHideFloatingWidget(widget.el);
    if (isTemporarilySuppressedRef.current !== temporarilySuppressed) {
      applyFloatingWidgetSuppression(widget.el, temporarilySuppressed);
      isTemporarilySuppressedRef.current = temporarilySuppressed;
    }
    publishWidgetMetrics(widget.el);
  }, [shouldHideTrigger]);

  return null;
}
